import React from 'react';
import { Select, Input, Switch, Button } from 'antd';
import { Search, Filter, RotateCcw, Library, Package2 } from 'lucide-react';
import { FilterState } from '../../types/pokemon';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { useI18n } from '../../contexts/I18nContext';
import { translateEnergyType } from '../../utils/energyUtils';

interface SetInfo {
  name: string;
  display_name: string;
  display_name_en?: string;
  img: string | null;
}

interface PackInfo {
  name: string;
  img: string | null;
}

// Configuration des raretés avec leur ordre et style
const rarityConfig: Record<string, { icon: string, color: string, order: number }> = {
  '♛': { icon: '♛', color: 'text-yellow-600', order: 1 },
  '☆☆☆': { icon: '★★★', color: 'text-purple-600', order: 2 },
  '☆☆': { icon: '★★', color: 'text-blue-600', order: 3 },
  '☆': { icon: '★', color: 'text-indigo-600', order: 4 },
  '♢♢♢♢': { icon: '♦♦♦♦', color: 'text-emerald-600', order: 5 },
  '♢♢♢': { icon: '♦♦♦', color: 'text-green-600', order: 6 },
  '♢♢': { icon: '♦♦', color: 'text-teal-600', order: 7 },
  '♢': { icon: '♦', color: 'text-cyan-600', order: 8 },
  '✧': { icon: '✧', color: 'text-amber-500', order: 3.5 },     // Shiny (1 icône) - ordre entre ☆☆ et ☆
  '✧✧': { icon: '✧✧', color: 'text-amber-600', order: 2.5 }    // Shiny (2 icônes) - ordre entre ☆☆☆ et ☆☆
};

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  packs: PackInfo[];
  rarities: string[];
  types: string[];
  energies: string[];
  sets: SetInfo[];
  className?: string;
  showMissingToggle?: boolean;
}

export function FilterBar({
  filters,
  onFilterChange,
  packs = [],
  rarities = [],
  types = [],
  energies = [],
  sets = [],
  className = '',
  showMissingToggle = true
}: FilterBarProps) {
  const { t, lang } = useI18n();

  const handleFilterChange = <K extends keyof FilterState>(
    key: K, 
    value: K extends 'showMissing' ? boolean : (string | null)
  ) => {
    if (key === 'set') {
      // Si on change de set, on réinitialise le pack
      onFilterChange({
        ...filters,
        [key]: value,
        pack: null // Réinitialiser le pack quand on change de set
      });
    } else {
      onFilterChange({
        ...filters,
        [key]: value
      });
    }
  };

  const handleReset = () => {
    onFilterChange({
      pack: null,
      rarity: null,
      set: null,
      search: '',
      type: null,
      energy: null,
      showMissing: false
    });
  };

  const hasActiveFilters = filters.pack !== null || 
    filters.rarity !== null || 
    filters.set !== null || 
    filters.search !== '' || 
    filters.type !== null || 
    filters.energy !== null || 
    filters.showMissing;

  // Déterminer si le filtre d'énergie doit être désactivé
  const isEnergyDisabled = filters.type === 'Objet' || filters.type === 'Supporter';

  // Trier les raretés selon l'ordre défini
  const sortedRarities = [...rarities].sort((a, b) => {
    const orderA = rarityConfig[a]?.order ?? 999;
    const orderB = rarityConfig[b]?.order ?? 999;
    return orderA - orderB;
  });

  const getSetDisplayName = (setName: string) => {
    const setInfo = sets?.find((set) => set.name === setName);
    if (!setInfo) return setName;
    
    return lang === 'en' && setInfo.display_name_en 
      ? setInfo.display_name_en 
      : setInfo.display_name;
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 ${className}`}>
      <div className="flex flex-col gap-4">
        {/* Barre de recherche et bouton de réinitialisation */}
        <div className="flex gap-3 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder={t('filters.search_placeholder')}
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="pl-10 h-10"
              allowClear
            />
          </div>
          {hasActiveFilters && (
            <Button
              icon={<RotateCcw className="w-4 h-4" />}
              onClick={handleReset}
              className="flex items-center gap-2 h-10"
            >
              {t('filters.reset_button')}
            </Button>
          )}
        </div>

        {/* Filtres principaux */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          {sets && sets.length > 0 && (
            <Select
              placeholder={t('filters.filter_set')}
              value={filters.set}
              onChange={(value) => handleFilterChange('set', value)}
              allowClear
              className="w-full !min-h-[40px]"
              options={(sets || []).map(set => ({
                label: (
                  <div className="flex items-center gap-2">
                    {set.img ? (
                      <img
                        src={`${SUPABASE_IMAGE_BASE_URL}${lang === 'en' ? 'en/' : ''}${set.img}`}
                        alt={getSetDisplayName(set.name)}
                        className="w-6 h-6 object-contain"
                      />
                    ) : (
                      <Library className="w-5 h-5 text-gray-400" />
                    )}
                    <span>{getSetDisplayName(set.name)}</span>
                  </div>
                ),
                value: set.name
              }))}
              optionLabelProp="label"
              listHeight={300}
            />
          )}

          <Select
            placeholder={t('filters.filter_pack')}
            value={filters.pack}
            onChange={(value) => handleFilterChange('pack', value)}
            allowClear
            className="w-full !min-h-[40px]"
            options={(packs || []).map(pack => ({
              label: (
                <div className="flex items-center gap-2">
                  {pack.img ? (
                    <img
                      src={`${SUPABASE_IMAGE_BASE_URL}${pack.img}`}
                      alt={pack.name}
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <Package2 className="w-5 h-5 text-gray-400" />
                  )}
                  <span>{pack.name}</span>
                </div>
              ),
              value: pack.name
            }))}
            optionLabelProp="label"
            listHeight={300}
          />

          <Select
            placeholder={t('filters.filter_rarity')}
            value={filters.rarity}
            onChange={(value) => handleFilterChange('rarity', value)}
            allowClear
            className="w-full !min-h-[40px]"
            options={(sortedRarities || []).map(rarity => ({
              label: (
                <span className={rarityConfig[rarity]?.color ?? ''}>
                  {rarityConfig[rarity]?.icon ?? rarity}
                </span>
              ),
              value: rarity
            }))}
          />

          <Select
            placeholder={t('filters.filter_type')}
            value={filters.type}
            onChange={(value) => handleFilterChange('type', value)}
            allowClear
            className="w-full !min-h-[40px]"
            options={(types || []).map(type => ({ label: type, value: type }))}
          />

          <Select
            placeholder={t('filters.filter_energy')}
            value={filters.energy}
            onChange={(value) => handleFilterChange('energy', value)}
            allowClear
            disabled={isEnergyDisabled}
            className="w-full !min-h-[40px]"
            options={(energies || []).map(energy => ({ 
              label: translateEnergyType(energy, lang as 'fr' | 'en') || energy, 
              value: energy 
            }))}
          />
        </div>

        {/* Filtres supplémentaires */}
        {showMissingToggle && (
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-500 font-medium">{t('filters.additional_filters')}</span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{t('filters.missing_cards')}</span>
              <Switch
                size="default"
                checked={filters.showMissing}
                onChange={(checked) => handleFilterChange('showMissing', checked)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}